import { lazy, FC, Suspense } from 'react'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { MasterLayout } from '../../_payairo/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper'
import { getCSSVariableValue } from '../../_payairo/assets/ts/_utils'
import { WithChildren } from '../../_payairo/helpers'
import { UserManagementWrapper } from '../pages/userManagement/UserManagement'
import { WithdrawHistoryWrapper } from '../pages/transactions/WithdrawHistory'
import { RolesManagementWrapper } from '../pages/rolesManagement/RolesManagement'
import { CustomerSupportDashboardWrapper } from '../pages/customerSupport/CustomerSupportDashboard'
import { useAuth } from '../modules/auth'
import { getDashboardPath, isManagerRole, isSpectatorOrDeveloperRole } from '../utils/helper'
import { ProtectedRoute } from '../App'

// Route guard component to redirect based on user role
const DashboardRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, auth } = useAuth()
  const location = useLocation()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const userType = currentUser?.user_type || auth?.user_type
  const path = location.pathname

  const isAdmin =
    role === 'SUPER_ADMIN' ||
    role === 'SuperAdmin' ||
    role === 'Admin' ||
    role === 'ADMIN' ||
    role === 'Super Admin' ||
    userType === 'Admin' ||
    userType === 'ADMIN'

  const isManager = isManagerRole(role, userType)
  const isSpectatorOrDev = isSpectatorOrDeveloperRole(role, userType)

  // If CUSTOMER_SUPPORT tries to access wrong dashboard, redirect
  if (role === 'CUSTOMER_SUPPORT' || role === 'CustomerSupport') {
    if (
      path === '/dashboard' ||
      path === '/super-admin/dashboard' ||
      path === '/spectator/dashboard' ||
      path === '/manager/dashboard'
    ) {
      return <Navigate to='/customer-support/dashboard' replace />
    }
  }

  // If ADMIN tries to access wrong dashboard, redirect
  if (isAdmin) {
    if (
      path === '/dashboard' ||
      path === '/customer-support/dashboard' ||
      path === '/spectator/dashboard' ||
      path === '/manager/dashboard'
    ) {
      return <Navigate to='/super-admin/dashboard' replace />
    }
  }

  // Manager → /manager/dashboard (not /spectator/dashboard)
  if (isManager) {
    if (
      path === '/dashboard' ||
      path === '/super-admin/dashboard' ||
      path === '/customer-support/dashboard' ||
      path === '/spectator/dashboard'
    ) {
      return <Navigate to='/manager/dashboard' replace />
    }
  }

  // Spectator / Developer → /spectator/dashboard (not /manager/dashboard)
  if (isSpectatorOrDev) {
    if (
      path === '/dashboard' ||
      path === '/super-admin/dashboard' ||
      path === '/customer-support/dashboard' ||
      path === '/manager/dashboard'
    ) {
      return <Navigate to='/spectator/dashboard' replace />
    }
  }

  return children
}

// Route guard: only CustomerSupport can access
const CustomerSupportOnlyRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, auth } = useAuth()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const userType = currentUser?.user_type || auth?.user_type
  const dashboardPath = getDashboardPath(role, currentUser?.user_type)

  const isCustomerSupport =
    role === 'CUSTOMER_SUPPORT' ||
    role === 'CustomerSupport' ||
    userType === 'CustomerSupport'

  if (!isCustomerSupport) {
    return <Navigate to={dashboardPath} replace />
  }

  return children
}

// Route guard: only Admin roles can access
// Must match SidebarMenuMain: admin-only items (All Users, Role Management, Set Limits, Fee Settings)
// are shown when user is NOT Customer Support and NOT Spectator — use the same rule here so
// API role strings (SUPER_ADMIN, Super Admin, etc.) don't incorrectly bounce users to dashboard.
const AdminOnlyRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, auth } = useAuth()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const userType = currentUser?.user_type || auth?.user_type
  const dashboardPath = getDashboardPath(role, currentUser?.user_type)

  const isCustomerSupport =
    role === 'CUSTOMER_SUPPORT' ||
    role === 'CustomerSupport' ||
    userType === 'CustomerSupport'

  const isSpectator =
    role === 'Spectator' ||
    userType === 'Spectator'

  const isManager =
    role === 'Manager' ||
    userType === 'Manager'

  // Spectator & Manager: same as sidebar — no admin-only pages (All Users, Role Management, limits, fees)
  const canAccessAdminOnlyRoutes = !isCustomerSupport && !isSpectator && !isManager

  if (!canAccessAdminOnlyRoutes) {
    return <Navigate to={dashboardPath} replace />
  }

  return children
}

// Role Management: Super Admin / Admin (same as admin-only scope) OR Manager — not CS / Spectator / Developer
const RoleManagementRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => {
  const { currentUser, auth } = useAuth()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const userType = currentUser?.user_type || auth?.user_type
  const dashboardPath = getDashboardPath(role, currentUser?.user_type)

  const isCustomerSupport =
    role === 'CUSTOMER_SUPPORT' ||
    role === 'CustomerSupport' ||
    userType === 'CustomerSupport'

  const isSpectator =
    role === 'Spectator' ||
    userType === 'Spectator'

  const isManager =
    role === 'Manager' ||
    userType === 'Manager'

  const canAccessAdminOnlyPages = !isCustomerSupport && !isSpectator && !isManager
  /** Same users as admin-only routes (e.g. Super Admin, Developer) OR Manager */
  const canAccessRoleManagement = canAccessAdminOnlyPages || isManager

  if (!canAccessRoleManagement) {
    return <Navigate to={dashboardPath} replace />
  }

  return children
}

// Route guard: shared staff pages (transactions, announcements, support, settings, notifications, …)
// Sidebar shows these for Admin, Spectator, and Customer Support — allow all authenticated app users here.
const AdminAndSpectatorRouteGuard: FC<{ children: React.ReactElement }> = ({ children }) => {
  return children
}

const PrivateRoutes = () => {
  const { currentUser, auth } = useAuth()
  const role = currentUser?.role || auth?.role || currentUser?.user_type
  const dashboardPath = getDashboardPath(role, currentUser?.user_type)

  const AnnouncementManagement = lazy(() => import('../pages/announcement/AnnouncementManagement'))
  const PayAiroUserManagement = lazy(() => import('../pages/payairouser/PayAiroUserManagement'))
  const SetLimitsWrapper = lazy(() => import('../pages/setLimits/SetLimits'))
  const FeeSettingsWrapper = lazy(() => import('../pages/feeSettings/FeeSettings'))
  const SupportCenterWrapper = lazy(() => import('../pages/supportCenter/Support'))
  const TransactionHistoryWrapper = lazy(() => import('../pages/transactions/AllTransactions'))
  const UserDetailsPage = lazy(() => import('../pages/userDetails/UserDetailsPage'))
  const MyProfileWrapper = lazy(() => import('../pages/myProfile/MyProfile'))
  const CryptoTransactionHistoryWrapper = lazy(() => import('../pages/transactions/CryptoTransactions'))
  const NotificationsPage = lazy(() => import('../pages/notifications/NotificationsPage'))
  const SpectatorDashboard = lazy(() => import('../pages/spectator/SpectatorDashboard'))
  const SupportPerformanceWrapper = lazy(() => import('../pages/supportPerformance/SupportPerformance'))

  return (
    <SuspensedView>
      <Routes>
        <Route element={<MasterLayout />}>
          {/* Redirect to Dashboard after success login/registartion */}
          <Route path='auth/*' element={<Navigate to={dashboardPath} replace />} />
          {/* Pages */}
          {/* Super Admin Dashboard */}
          <Route
            path='super-admin/dashboard'
            element={
              <DashboardRouteGuard>
                <DashboardWrapper />
              </DashboardRouteGuard>
            }
          />
          {/* Legacy dashboard route - redirects based on role */}
          <Route
            path='dashboard'
            element={
              <DashboardRouteGuard>
                <DashboardWrapper />
              </DashboardRouteGuard>
            }
          />
          {/* Customer Support Dashboard */}
          <Route
            path='customer-support/dashboard'
            element={
              <DashboardRouteGuard>
                <CustomerSupportDashboardWrapper />
              </DashboardRouteGuard>
            }
          />
          {/* Manager Dashboard — same UI/API as Spectator; URL /manager/dashboard */}
          <Route
            path='manager/dashboard'
            element={
              <ProtectedRoute allowedRoles={['Manager']}>
                <DashboardRouteGuard>
                  <SpectatorDashboard />
                </DashboardRouteGuard>
              </ProtectedRoute>
            }
          />
          {/* Spectator Dashboard — Spectator & Developer only */}
          <Route
            path='spectator/dashboard'
            element={
              <ProtectedRoute allowedRoles={['Spectator', 'Developer']}>
                <DashboardRouteGuard>
                  <SpectatorDashboard />
                </DashboardRouteGuard>
              </ProtectedRoute>
            }
          />
          {/* Customer Support PayAiro Users - separate route, same data */}
          <Route
            path='customer-support/payairo-users'
            element={
              <CustomerSupportOnlyRouteGuard>
                <PayAiroUserManagement />
              </CustomerSupportOnlyRouteGuard>
            }
          />
          <Route
            path='all-users'
            element={
              <AdminOnlyRouteGuard>
                <UserManagementWrapper />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='transactions/withdraw'
            element={
              <AdminAndSpectatorRouteGuard>
                <WithdrawHistoryWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='role-management'
            element={
              <RoleManagementRouteGuard>
                <RolesManagementWrapper />
              </RoleManagementRouteGuard>
            }
          />

          <Route
            path='transactions/fiat'
            element={
              <AdminAndSpectatorRouteGuard>
                <TransactionHistoryWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='transactions/crypto'
            element={
              <AdminAndSpectatorRouteGuard>
                <CryptoTransactionHistoryWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='settings'
            element={
              <AdminAndSpectatorRouteGuard>
                <MyProfileWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='admin/support-performance'
            element={
              <AdminOnlyRouteGuard>
                <SupportPerformanceWrapper />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='manager/support-performance'
            element={
              <ProtectedRoute allowedRoles={['Manager']}>
                <DashboardRouteGuard>
                  <SupportPerformanceWrapper />
                </DashboardRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path='announcements'
            element={
              <AdminAndSpectatorRouteGuard>
                <AnnouncementManagement />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='payairo-users'
            element={
              <AdminAndSpectatorRouteGuard>
                <PayAiroUserManagement />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='set-limits'
            element={
              <AdminOnlyRouteGuard>
                <SetLimitsWrapper />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='fee-settings'
            element={
              <AdminOnlyRouteGuard>
                <FeeSettingsWrapper />
              </AdminOnlyRouteGuard>
            }
          />
          <Route
            path='support'
            element={
              <AdminAndSpectatorRouteGuard>
                <SupportCenterWrapper />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='notifications'
            element={
              <AdminAndSpectatorRouteGuard>
                <NotificationsPage />
              </AdminAndSpectatorRouteGuard>
            }
          />
          <Route
            path='user/:id'
            element={
              <AdminAndSpectatorRouteGuard>
                <UserDetailsPage />
              </AdminAndSpectatorRouteGuard>
            }
          />


          {/* <Route path='urlregistration' element={<UrlRegistration />} />
        <Route path='urlmapping' element={<UrlMapping />} />
        <Route path='myip' element={<MyIP />} />
        <Route path='vendorlist' element={<VendorListWrapper />} />
        <Route path='manageroutes' element={<ManageRoutes />} />
        <Route path='manageroutetree' element={<ManageRoutesTree />} /> */}
          {/* <Route path='ucccomplaints' element={<UccComplaintsWrapper />} /> */}
          {/* <Route path='smspack' element={<SMSPackWrapper />} /> */}
          {/* <Route path='orderslist' element={<OrdersListWrapper />} />
        <Route path='invoice' element={<InvoiceWrapper />} />
        <Route path='bulk-report' element={<BulkReport />} /> */}

          {/* Lazy Modules */}
          {/* <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/pages/wizards/*'
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/widgets/*'
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        /> */}
          {/* Page Not Found */}
          <Route path='*' element={<Navigate to='/error/404' />} />
        </Route>

        {/* <Route element={<PaymentLayout />}>
        <Route path='payment' element={<Payment />} />
      </Route> */}
      </Routes>

    </SuspensedView>
  )
}

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }